import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { get } from '@/lib/api';
import { storePath } from '@/lib/store';
import type { StoreRevision } from '@/lib/revision-queue';
import { ChangeRequestList, type ChangeRequest, type ChangeRequestType } from './ChangeRequestList';

// The team-store twin of SubmissionWorkerDrawer: every request on the store
// (open and closed, with resolutions), actionable in place.
export function StoreRevisionDrawer({
  item,
  onClose,
  onChanged,
}: {
  item: StoreRevision | null;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const storeId = item?.storeId ?? null;
  const { data: catalog } = useSWR<{ types: ChangeRequestType[] }>(
    storeId ? '/api/team-stores/request-types' : null, get,
  );
  const { data, error, isLoading, mutate } = useSWR<{ requests: ChangeRequest[] }>(
    storeId ? `/api/team-stores/${encodeURIComponent(storeId)}/requests` : null, get,
  );

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[480px] overflow-y-auto sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{item?.storeName || 'Team store'}</SheetTitle>
        </SheetHeader>
        {storeId ? (
          <div className="mt-4 space-y-3">
            <Link to={storePath(storeId)} onClick={onClose} className="text-[12px] text-primary underline">
              Open the store page →
            </Link>
            {error && !data ? (
              <p className="text-[12px] text-danger">Couldn't load this store's requests — close and reopen to retry.</p>
            ) : isLoading ? (
              <p className="text-[12px] text-muted">Loading…</p>
            ) : (
              <ChangeRequestList
                storeId={storeId}
                requests={data?.requests ?? []}
                types={catalog?.types ?? []}
                onChanged={async () => { await Promise.all([mutate(), onChanged()]); }}
              />
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
